/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IComponentNodeProps, IModuleProps, INodeProps, ISingleSlideCarouselProps, ITelemetryContent, NodeTag, SingleSlideCarousel } from '@msdyn365-commerce-modules/utilities';
import { ProductComponent } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ProductPrice, ProductSearchResult, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import {
    getActivePricesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { AffiliationLoyaltyTier, CustomerAffiliation } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import * as React from 'react';
import { IProductCollectionProps, layout } from './product-collection.props.autogenerated';
export interface IProductCollectionViewProps extends IProductCollectionProps<{}> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResult[];
}
/**
 * ProductCollection component
 */
class ProductCollection extends React.PureComponent<IProductCollectionProps<{}>,IProductComponentState> {
    private telemetryContent: ITelemetryContent;
    private productPrices: ProductPrice[];
    constructor(props: IProductCollectionProps<{}>) {
        super(props);
        this.productPrices=[];
        this.state = {
            products: this.props.config.productCollection && this.props.config.productCollection.products,
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
                                                   this.props.friendlyName,
                                                   this.props.telemetry);
    }
    public componentDidMount(): void {
        if (this.props.config.enableAffiliationBasedPricing &&
            this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
            // tslint:disable-next-line:no-floating-promises
            this._getActivePrice();
        }
    }
    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { resources } = this.props;
        const products = this.state.products;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={heading.text}
                editProps={{onEdit:this.handleHeadingChange, requestContext:this.props.context.request}}
            />
        );

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className:  classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items',
            },
            isCarousel: isCarousel,
            products: products && products.length > 0 && products.map((item: ProductSearchResult, index: number) => this._getProduct(item, index)),
            productPrices: this.productPrices,
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => this.props.config.heading!.text = event.target.value;

    private _getProduct = (product:ProductSearchResult, index: number): IProductComponentViewProps | null => {
        if(!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
                                                    this.props.friendlyName,
                                                    this.props.telemetry);
        const { imageSettings, allowBackNavigation } = this.props.config;
        const resources = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: index,
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{product: product}}
                />
            )
        };
    }
    private _getActivePrice = async (): Promise<void> => {
         let products = (this.state.products) || [];
         const productRecIds = (products && products.length > 0) ? products.map(prod => prod.RecordId) : [];
         const projectDomain: ProjectionDomain = { ChannelId: this.props.context.request.apiSettings.channelId, CatalogId: this.props.context.request.apiSettings.catalogId };
         const affliations: AffiliationLoyaltyTier[] = [];
         const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
         const customer = await getCustomer(customerInput, this.props.context.actionContext);

         if (customer && customer.CustomerAffiliations) {
                customer.CustomerAffiliations.forEach((affliation: CustomerAffiliation) => {
                    const aff: AffiliationLoyaltyTier = { AffiliationId: affliation.RetailAffiliationId, CustomerId: customer.AccountNumber };
                    affliations.push(aff);
                });
            }

         await getActivePricesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affliations,
            true
            ).then((activePricesResponse:ProductPrice[]) => {
            this.productPrices = activePricesResponse;
            products = products.map(prod => {
                    const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(activePrice.BasePrice || 0, activePrice.TradeAgreementPrice || 0, activePrice.AdjustedPrice || 0);
                    }
                    return prod;
                });
            this.setState({
                    products:products
                });
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(`[get Affiliation based price on Product Collection]Error executing action`);
                throw new Error('[get Affiliation based price on Product Collection]Error executing action');
            });
    }
}

export default ProductCollection;
